// Derived numbers for the owner's reporting tabs. Every one of these used to
// live inline inside the tab that rendered it, which is how the same question
// — "what does date === null mean?" — got three different answers, two of them
// wrong: TrendsTab counted unplaced tiles as overdue backlog, and
// OwnerSchoolModule's assignment sort crashed on them outright.
//
// Pure functions over (assignments, calendar). No React, no Firebase.
import { addDaysIso } from './calendarMath';

// The single rule the whole file depends on: a tile with no date isn't ON a
// day, so it can't be part of any per-day or per-date question. It is not
// overdue, not scheduled, and doesn't count toward attendance. Its own count
// is `unplacedForWeek` below.
export function bucketByDate(assignments) {
  const m = new Map();
  for (const a of assignments) {
    if (!a.date) continue;
    if (!m.has(a.date)) m.set(a.date, []);
    m.get(a.date).push(a);
  }
  return m;
}

export function plannedVsCompleted(items) {
  let plannedMinutes = 0;
  let completedMinutes = 0;
  for (const a of items) {
    const m = a.estMinutes || 0;
    plannedMinutes += m;
    if (a.status === 'complete') completedMinutes += m;
  }
  return { plannedMinutes, completedMinutes };
}

export function unplacedForWeek(assignments, weekNumber) {
  return assignments.filter(
    a => a.placementMode === 'student-placed' && a.weekNumber === weekNumber && !a.date
  );
}

// Days with nothing scheduled, or nothing that counts toward the must-do floor.
export function coverageWeeks(assignments, calendar, { currentWeek, before = 1, after = 3 }) {
  const byDate = bucketByDate(assignments);
  const startWeek = Math.max(1, currentWeek - before);
  const endWeek = Math.min(calendar.weeks.length, currentWeek + after);

  const weeks = [];
  for (let wn = startWeek; wn <= endWeek; wn++) {
    const monday = calendar.weeks[wn - 1];
    const days = [];
    for (let dow = 1; dow <= 5; dow++) {
      const date = addDaysIso(monday, dow - 1);
      if (calendar.holidays?.[date]) continue;
      const items = byDate.get(date) || [];
      const floorCount = items.filter(a => a.isFloor).length;
      days.push({
        date,
        count: items.length,
        floorCount,
        totalMinutes: items.reduce((s, a) => s + (a.estMinutes || 0), 0),
        flagged: items.length === 0 || floorCount === 0,
      });
    }
    weeks.push({ weekNumber: wn, days });
  }

  return { weeks, flaggedCount: weeks.reduce((s, w) => s + w.days.filter(d => d.flagged).length, 0) };
}

// Completion rate per week, for weeks that have actually started.
export function weeklyCompletion(assignments, { currentWeek, lookback = 8 }) {
  const firstWeek = Math.max(1, currentWeek - lookback + 1);
  const out = [];
  for (let wn = firstWeek; wn <= currentWeek; wn++) {
    const items = assignments.filter(a => a.weekNumber === wn);
    if (items.length === 0) continue;
    const complete = items.filter(a => a.status === 'complete').length;
    out.push({ weekNumber: wn, total: items.length, complete, pct: Math.round((complete / items.length) * 100) });
  }
  return out;
}

// When work actually gets finished, by weekday — the "most of it lands after
// Wednesday" signal. Index 0 = Sunday, matching Date#getDay.
export function completionByWeekday(assignments) {
  const byWeekday = [0, 0, 0, 0, 0, 0, 0];
  for (const a of assignments) {
    if (a.status !== 'complete' || !a.completedAt) continue;
    const d = new Date(a.completedAt);
    if (!isNaN(d)) byWeekday[d.getDay()]++;
  }
  return byWeekday;
}

// Unfinished work whose day has already passed. The `a.date &&` guard is the
// fix for the old `a.date >= today` test: with date === null that comparison
// is false (null coerces to 0, the date string to NaN), so every unplaced tile
// fell through and was reported as overdue — hundreds of them.
export function backlog(assignments, today) {
  const byWeek = {};
  let total = 0;
  for (const a of assignments) {
    if (a.status === 'complete') continue;
    if (!a.date || a.date >= today) continue;
    byWeek[a.weekNumber] = (byWeek[a.weekNumber] || 0) + 1;
    total++;
  }
  return {
    byWeek,
    weeks: Object.keys(byWeek).map(Number).sort((a, b) => a - b),
    total,
  };
}

// NJ instructional-days record. A day counts when it has at least one must-do
// item and every one of them is complete.
export function attendanceStats(assignments, calendar) {
  const byDate = bucketByDate(assignments);

  let totalCounted = 0;
  const weeks = calendar.weeks.map((monday, i) => {
    let counted = 0;
    let scheduled = 0;
    for (let d = 0; d < 5; d++) {
      const date = addDaysIso(monday, d);
      if (calendar.holidays?.[date]) continue;
      const items = byDate.get(date) || [];
      if (items.length === 0) continue; // nothing generated — not a scheduled day
      scheduled++;
      const floor = items.filter(a => a.isFloor);
      if (floor.length > 0 && floor.every(a => a.status === 'complete')) {
        counted++;
        totalCounted++;
      }
    }
    return { weekNumber: i + 1, start: monday, end: addDaysIso(monday, 4), counted, scheduled };
  });

  const totalMinutes = assignments
    .filter(a => a.status === 'complete')
    .reduce((s, a) => s + (a.estMinutes || 0), 0);

  return { weeks, totalCounted, totalHours: totalMinutes / 60 };
}

// How the current week is going, for the owner's dashboard.
export function weekSnapshot(assignments, calendar, weekNumber) {
  const items = assignments.filter(a => a.weekNumber === weekNumber);
  const placed = items.filter(a => a.date);
  const unplaced = unplacedForWeek(assignments, weekNumber);
  const byDate = bucketByDate(items);

  const monday = calendar?.weeks?.[weekNumber - 1];
  let floorDaysMet = 0;
  let schoolDays = 0;
  if (monday) {
    for (let d = 0; d < 5; d++) {
      const date = addDaysIso(monday, d);
      if (calendar.holidays?.[date]) continue;
      schoolDays++;
      const floor = (byDate.get(date) || []).filter(a => a.isFloor);
      if (floor.length > 0 && floor.every(a => a.status === 'complete')) floorDaysMet++;
    }
  }

  return {
    total: items.length,
    placedCount: placed.length,
    unplacedCount: unplaced.length,
    completeCount: items.filter(a => a.status === 'complete').length,
    floorDaysMet,
    schoolDays,
    ...plannedVsCompleted(placed),
  };
}
